// What a Slack connection is asked for, once (#601), as `jiraFields.tsx` does for Jira.
//
// The stepped flow that connects or edits one (`SlackFlow.tsx`, #601, #817) asks for
// these settings. Every label, hint and placeholder below is an inline literal pinned by
// an exact-string test, so what renders them renders these components rather than holding
// a copy. The reasons for the words are on #578, and for the token field's
// attributes on #571.
//
// What is wrong with a value stays where it was (`Integrations.tsx`,
// `slackChannelProblem` and `slackTokenProblem`): `tests/test_integration_setup.py`
// reads that file for the channel's two sentences, so these fields are handed the
// problem to show and do not decide it.

import { SEVERITIES, SEVERITY_LABEL, type Severity } from "../api/integrations";
import { Field } from "./chrome";
import { SLACK_MANIFEST_SCOPES } from "./slackManifest";

/** What a saved Slack connection is greeted with, by the flow's last step. */
export const SLACK_CONNECTED = "Slack connected. The token is stored encrypted.";

/** What the floor does not gate (`integrations/slack.py`, `_plan`). */
export const APPROVALS_ALWAYS_POST = "Approval requests always post, whatever this is set to.";

// The scopes are the manifest's own (#604), which `tests/test_integration_setup.py` holds to
// the adapter's calls: a token without `channels:read` posts, and cannot list channels.
const WHERE_A_BOT_TOKEN_IS = `Starts with xoxb-. In your Slack app: OAuth & Permissions → Bot User OAuth Token. The app needs the ${SLACK_MANIFEST_SCOPES.join(" and ")} scopes.`;

/** One field: what it holds, what is wrong with it, and where a keystroke and a leaving go. */
interface Holding {
  value: string;
  error: string | null;
  onType: (value: string) => void;
  /** The person left the field: say now what is wrong with what is in it. */
  onLeave: (value: string) => void;
}

export function SlackChannelField({ value, error, onType, onLeave }: Holding) {
  return (
    <Field
      id="slack-channel"
      label="Channel ID"
      required
      placeholder="C0123456789"
      hint="In Slack: open the channel → its name → About → Channel ID. Invite the app first: /invite @your-app."
      error={error}
      value={value}
      onChange={(event) => onType(event.target.value)}
      onBlur={(event) => onLeave(event.target.value)}
    />
  );
}

/** The conclusions threshold: a severity, or nothing chosen yet. */
export function SlackFloorField({ value, onChoose }: { value: Severity | ""; onChoose: (floor: Severity | "") => void }) {
  return (
    <p className="field">
      <label htmlFor="slack-severity-floor" className="field__label">
        Post conclusions at or above
      </label>
      <select id="slack-severity-floor" className="input" aria-describedby="slack-severity-floor-hint" required value={value} onChange={(event) => onChoose(event.target.value as Severity | "")}>
        <option value="">Choose a severity</option>
        {SEVERITIES.map((severity) => (
          <option key={severity} value={severity}>
            {SEVERITY_LABEL[severity]}
          </option>
        ))}
      </select>
      <small id="slack-severity-floor-hint" className="field__hint">
        {APPROVALS_ALWAYS_POST}
      </small>
    </p>
  );
}

/** The bot token: asked for whether connecting or replacing a saved one (#817). */
export function SlackTokenField({ value, error, onType, onLeave }: Holding) {
  return (
    <Field
      id="slack-bot-token"
      label="Bot User OAuth Token"
      type="password"
      required
      name="slack-bot-token"
      autoComplete="new-password"
      reveal
      hint={WHERE_A_BOT_TOKEN_IS}
      error={error}
      value={value}
      onChange={(event) => onType(event.target.value)}
      onBlur={(event) => onLeave(event.target.value)}
    />
  );
}
